/**
 * AI Assistant settings against the real contract (AI-CONNECT-001 / ERP-277 / ERP-278).
 *
 * Real tenant admin control for:
 *   - Access & Opt-In (`GET/PUT /activation`)
 *   - Data Scope (6 Yar Assistants & Read-Only ERP Safety)
 *   - Budget & Caps (`GET/PUT /budgets`)
 *   - Usage Report (`GET /usage?scope=all`)
 *   - Retention Period (`GET/PUT /settings`)
 */
import { useState } from 'react';
import {
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Tabs,
} from '@sakhtar/design-system/react';
import { displayJalaliDate, displayJalaliDateTime } from '../../../lib/date/displayJalali.js';
import { faDigits } from '../../../lib/faDigits.js';
import { useAiPreview } from '../aiPreview.js';
import { useAiProvider } from '../connect/AiProviderContext.js';
import type { AiBudget } from '../connect/contract.ts';
import { formatMoneyAsTokens } from '../connect/display.ts';
import { describeError, type DescribedError } from '../connect/errors.ts';
import { useAiResource } from './useAiResource.ts';
import './live.css';

type SettingsTabId = 'access' | 'scope' | 'caps' | 'usage' | 'retention';

const TABS: readonly { id: SettingsTabId; label: string }[] = [
  { id: 'access', label: 'دسترسی و فعال‌سازی' },
  { id: 'scope', label: 'محدودهٔ داده و یارها' },
  { id: 'caps', label: 'سقف بودجه' },
  { id: 'usage', label: 'گزارش مصرف' },
  { id: 'retention', label: 'نگهداری سابقه' },
];

const YAR_ASSISTANTS = [
  {
    key: 'inventory',
    title: 'انبار یار',
    tool: 'inventory.stock.summary',
    domain: 'انبار و کالا',
    description: 'تحلیل موجودی انبارها و گردش اقلام بر اساس داده‌های سرور.',
  },
  {
    key: 'sales',
    title: 'فروش یار',
    tool: 'crm.opportunity.open',
    domain: 'فروش و CRM',
    description: 'تحلیل فرصت‌های باز فروش و وضعیت پایپ‌لاین به ریال/تومان.',
  },
  {
    key: 'procurement',
    title: 'تدارکات یار',
    tool: 'procurement.order.open',
    domain: 'خرید و تأمین',
    description: 'تحلیل سفارش‌های باز خرید، تحویل‌نشده‌ها و مبالغ تأمین‌کنندگان.',
  },
  {
    key: 'finance',
    title: 'مالی یار',
    tool: 'accounting.open-items',
    domain: 'حسابداری و مالی',
    description: 'تحلیل اقلام باز دریافتنی و پرداختنی بدون تهاتر مطالبات و بدهی.',
  },
  {
    key: 'projects',
    title: 'پروژه یار',
    tool: 'projects.task.overdue',
    domain: 'پروژه‌ها و تسک‌ها',
    description: 'تحلیل کارهای باز دارای تأخیر بر حسب روز بدون تبدیل به واحد پولی.',
  },
  {
    key: 'sakhtar',
    title: 'ساختار یار',
    tool: 'sakhtar.composite',
    domain: 'تحلیل جامع چندماژولی',
    description: 'تجمیع امن میان‌حوزه‌ای ERP با تفکیک دقیق منابع و داده‌های سرور.',
  },
] as const;

export function LiveSettings() {
  const ai = useAiPreview();
  const [tab, setTab] = useState<SettingsTabId>('access');

  if (!ai.isTenantAdmin) {
    return (
      <StatePanel
        variant="permission_denied"
        title="تنظیمات دستیار فقط برای مدیر سازمان است"
        description="برای تغییر این تنظیمات با مدیر سازمان خود تماس بگیرید."
      />
    );
  }

  return (
    <div className="ai-live">
      <V2Card className="ai-live__card">
        <V2CardHeader title="تنظیمات دستیار هوشمند و ساختاریار" icon="settings" />
        <div className="ai-live__pad">
          <V2Tabs activeId={tab} items={TABS} onChange={(id) => setTab(id as SettingsTabId)} />
        </div>
      </V2Card>

      {tab === 'access' ? <AccessTab /> : null}
      {tab === 'scope' ? <ScopeTab /> : null}
      {tab === 'caps' ? <CapsTab /> : null}
      {tab === 'usage' ? <UsageTab /> : null}
      {tab === 'retention' ? <RetentionTab /> : null}
    </div>
  );
}

function AccessTab() {
  const provider = useAiProvider();
  const activation = useAiResource((signal) => provider.getActivation({ signal }), [provider]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);

  async function toggleOptIn(nextState: boolean) {
    setBusy(true);
    setError(null);
    try {
      await provider.setOptIn(nextState);
      activation.reload();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  if (activation.state.status === 'loading') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="status">
          در حال دریافت وضعیت دستیار…
        </div>
      </V2Card>
    );
  }

  if (activation.state.status === 'error') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="alert">
          <p>{activation.state.error.message}</p>
          <V2Button variant="primary" size="sm" onClick={activation.reload}>
            تلاش دوباره
          </V2Button>
        </div>
      </V2Card>
    );
  }

  const data = activation.state.data;

  return (
    <div className="ai-live">
      <V2Card>
        <V2CardHeader title="وضعیت فعال‌سازی دستیار در سازمان" icon="shield" />
        <div className="ai-live__pad">
          <div className="ai-live__row">
            <span style={{ fontWeight: 600 }}>وضعیت سرویس سازمان:</span>
            <V2Badge tone={data.active ? 'success' : 'neutral'}>
              {data.active ? 'فعال' : 'غیرفعال'}
            </V2Badge>
            {data.killSwitch ? <V2Badge tone="danger">متوقف‌شده از سمت پلتفرم</V2Badge> : null}
          </div>

          <div className="ai-live__row" style={{ marginTop: 8 }}>
            <span>رضایت و فعال‌سازی سازمان (Opt-in):</span>
            <V2Badge tone={data.optedIn ? 'success' : 'warn'}>
              {data.optedIn ? 'فعال‌شده' : 'غیرفعال'}
            </V2Badge>
            <V2Button
              variant={data.optedIn ? 'ghost' : 'primary'}
              size="sm"
              disabled={busy || data.killSwitch}
              onClick={() => void toggleOptIn(!data.optedIn)}
            >
              {data.optedIn ? 'غیرفعال‌سازی هوش مصنوعی' : 'فعال‌سازی هوش مصنوعی سازمان'}
            </V2Button>
          </div>

          <p className="ai-live__hint" style={{ marginTop: 8 }}>
            فعال‌سازی دستیار اجازه می‌دهد کاربران مجاز سازمان بر اساس نقش و دسترسی‌های اختصاصی، از
            یارهای هوشمند برای تحلیل داده‌ها استفاده نمایند.
          </p>

          {error ? (
            <p role="alert" style={{ color: 'var(--sk-danger)' }}>
              {error.message}
              {error.requestId ? ` (کد پیگیری: ${error.requestId})` : ''}
            </p>
          ) : null}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="قابلیت‌های ثبت‌شده در سرور" icon="layers" />
        <div className="ai-live__pad">
          <ul className="ai-live__hint" style={{ margin: 0, paddingRight: 20 }}>
            {data.features.map((f) => (
              <li key={f.code} style={{ marginBottom: 4 }}>
                <code style={{ direction: 'ltr', display: 'inline-block' }}>{f.code}</code>
                {' — '}
                {f.executable ? (
                  <span style={{ color: 'var(--sk-success)' }}>عملیاتی و قابل اجرا</span>
                ) : (
                  <span style={{ color: 'var(--sk-fg-muted)' }}>در دسترس نیست</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </V2Card>
    </div>
  );
}

function ScopeTab() {
  const provider = useAiProvider();
  const activation = useAiResource((signal) => provider.getActivation({ signal }), [provider]);
  const active = activation.state.status === 'ready' && activation.state.data.active;

  return (
    <div className="ai-live">
      <V2Card>
        <V2CardHeader title="یارهای هوشمند شش‌گانه سازمان" icon="users" />
        <div className="ai-live__pad">
          <p className="ai-live__hint" style={{ marginBottom: 12 }}>
            هر یار در حوزهٔ تخصصی خود بر اساس اصول DENY-first فقط داده‌های مجاز سرور را می‌خواند و
            هیچ عمل ویرایش، ثبت سند، پرداخت یا تغییر موجودی انجام نمی‌دهد.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {YAR_ASSISTANTS.map((yar) => (
              <div
                key={yar.key}
                style={{
                  border: '1px solid var(--sk-border)',
                  borderRadius: 8,
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  background: 'var(--sk-bg-subtle, transparent)',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontWeight: 600 }}>{yar.title}</span>
                  <V2Badge tone={active ? 'success' : 'neutral'}>
                    {active ? 'متصل و فعال' : 'غیرفعال'}
                  </V2Badge>
                </div>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  حوزه: {yar.domain}
                </span>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>{yar.description}</p>
                <code
                  style={{
                    fontSize: 11,
                    direction: 'ltr',
                    color: 'var(--sk-fg-muted)',
                    marginTop: 'auto',
                  }}
                >
                  {yar.tool}
                </code>
              </div>
            ))}
          </div>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="مرزهای امنیتی و حفاظت از داده‌ها" icon="lock" />
        <div className="ai-live__pad">
          <ul className="ai-live__hint" style={{ margin: 0, paddingRight: 20, lineHeight: 1.8 }}>
            <li>
              <strong>فقط‌خواندنی:</strong> هوش مصنوعی هیچ تراکنش مالی، تغییر موجودی، یا تغییر رکورد
              ایجاد نمی‌کند.
            </li>
            <li>
              <strong>عدم تهاتر:</strong> مطالبات و بدهی‌های مالی هرگز تهاتر یا جمع نمی‌شوند.
            </li>
            <li>
              <strong>تفکیک واحدها:</strong> روزهای تأخیر تسک‌ها با مبالغ مالی ترکیب نمی‌شوند.
            </li>
            <li>
              <strong>حفاظت از کلیدها:</strong> متون ورودی و خروجی پیش از پردازش Redact شده و
              توکن‌ها و رمزها سانسور می‌شوند.
            </li>
          </ul>
        </div>
      </V2Card>
    </div>
  );
}

function CapsTab() {
  const provider = useAiProvider();
  const budgets = useAiResource((signal) => provider.getBudget({ signal }), [provider]);
  const [hardCredits, setHardCredits] = useState('');
  const [softCredits, setSoftCredits] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);

  const tenantBudget: AiBudget | undefined =
    budgets.state.status === 'ready'
      ? (budgets.state.data.find((b) => b.scope === 'tenant') ?? budgets.state.data[0])
      : undefined;

  async function saveBudget() {
    if (!tenantBudget) return;
    const hard = hardCredits.trim();
    const soft = softCredits.trim();
    if (!/^[0-9]+$/.test(hard) || !/^[0-9]+$/.test(soft)) return;
    if (BigInt(soft) > BigInt(hard)) return;

    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await provider.updateBudget({
        scope: tenantBudget.scope,
        scopeKey: tenantBudget.scopeKey,
        hardCredits: hard,
        softCredits: soft,
      });
      setSaved(true);
      budgets.reload();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  if (budgets.state.status === 'loading') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="status">
          در حال دریافت اطلاعات بودجه…
        </div>
      </V2Card>
    );
  }

  return (
    <div className="ai-live">
      <V2Card>
        <V2CardHeader title="سقف بودجه و مصرف دوره جاری" icon="finance" />
        <div className="ai-live__pad">
          {tenantBudget ? (
            <>
              <div className="ai-live__row" style={{ marginBottom: 12 }}>
                <span>دوره بودجه:</span>
                <span style={{ fontWeight: 600 }}>
                  از {displayJalaliDate(tenantBudget.period.start)} تا{' '}
                  {displayJalaliDate(tenantBudget.period.end)}
                </span>
                <V2Badge tone="neutral">منطقه: {tenantBudget.period.zone}</V2Badge>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                    سقف سخت (قطع اجرا)
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {formatMoneyAsTokens(tenantBudget.hard)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                    {faDigits(tenantBudget.hardCredits)} اعتبار
                  </div>
                </div>

                <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>سقف نرم (هشدار)</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {formatMoneyAsTokens(tenantBudget.soft)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                    {faDigits(tenantBudget.softCredits)} اعتبار
                  </div>
                </div>

                <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>مصرف‌شده تاکنون</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {formatMoneyAsTokens(tenantBudget.consumed)}
                  </div>
                </div>

                <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>سهمیه رزرو باز</div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                    {formatMoneyAsTokens(tenantBudget.openHolds)}
                  </div>
                </div>

                <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>مانده تا سقف سخت</div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginTop: 4,
                      color: 'var(--sk-success)',
                    }}
                  >
                    {formatMoneyAsTokens(tenantBudget.remaining)}
                  </div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--sk-border)', paddingTop: 14 }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>تنظیم سقف جدید برای سازمان</h4>
                <div className="ai-live__row" style={{ gap: 14 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12 }}>سقف سخت (اعتبار):</span>
                    <input
                      className="sk-input"
                      type="text"
                      inputMode="numeric"
                      placeholder={tenantBudget.hardCredits}
                      value={hardCredits || tenantBudget.hardCredits}
                      onChange={(e) => setHardCredits(e.target.value)}
                      style={{ width: 140 }}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12 }}>سقف نرم (اعتبار):</span>
                    <input
                      className="sk-input"
                      type="text"
                      inputMode="numeric"
                      placeholder={tenantBudget.softCredits}
                      value={softCredits || tenantBudget.softCredits}
                      onChange={(e) => setSoftCredits(e.target.value)}
                      style={{ width: 140 }}
                    />
                  </label>

                  <div style={{ alignSelf: 'flex-end' }}>
                    <V2Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => void saveBudget()}
                    >
                      ذخیره سقف بودجه
                    </V2Button>
                  </div>
                </div>

                {saved ? (
                  <p role="status" style={{ color: 'var(--sk-success)', marginTop: 8 }}>
                    سقف بودجه با موفقیت ذخیره شد.
                  </p>
                ) : null}
                {error ? (
                  <p role="alert" style={{ color: 'var(--sk-danger)', marginTop: 8 }}>
                    {error.message}
                    {error.requestId ? ` (کد پیگیری: ${error.requestId})` : ''}
                  </p>
                ) : null}
              </div>
            </>
          ) : (
            <V2EmptyState
              icon="finance"
              title="بودجه‌ای برای این دوره تعریف نشده است"
              description="بودجه‌های سازمان به طور خودکار با شروع مصرف مدیریت می‌شوند."
            />
          )}
        </div>
      </V2Card>
    </div>
  );
}

function UsageTab() {
  const provider = useAiProvider();
  const usage = useAiResource((signal) => provider.getUsage('all', { signal }), [provider]);

  if (usage.state.status === 'loading') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="status">
          در حال دریافت گزارش مصرف…
        </div>
      </V2Card>
    );
  }

  if (usage.state.status === 'error') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="alert">
          <p>{usage.state.error.message}</p>
          <V2Button variant="primary" size="sm" onClick={usage.reload}>
            تلاش دوباره
          </V2Button>
        </div>
      </V2Card>
    );
  }

  const items = usage.state.data.items;

  const totalInputTokens = items.reduce(
    (sum, row) => sum + (row.usage.inputTokens ? BigInt(row.usage.inputTokens) : 0n),
    0n,
  );
  const totalOutputTokens = items.reduce(
    (sum, row) => sum + (row.usage.outputTokens ? BigInt(row.usage.outputTokens) : 0n),
    0n,
  );
  const totalTokens = totalInputTokens + totalOutputTokens;

  return (
    <div className="ai-live">
      <V2Card>
        <V2CardHeader title="خلاصه گزارش مصرف سازمان" icon="trend" />
        <div className="ai-live__pad">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>مجموع توکن‌های مصرفی</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {faDigits(totalTokens.toString())} توکن
              </div>
            </div>

            <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>تعداد اجراها</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {faDigits(items.length)} نوبت
              </div>
            </div>

            <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>توکن‌های ورودی</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {faDigits(totalInputTokens.toString())}
              </div>
            </div>

            <div style={{ padding: 12, border: '1px solid var(--sk-border)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>توکن‌های خروجی</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
                {faDigits(totalOutputTokens.toString())}
              </div>
            </div>
          </div>

          <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>ریز نوبت‌های اجرا شده</h4>
          {items.length === 0 ? (
            <V2EmptyState
              icon="clock"
              title="هنوز اجرایی ثبت نشده است"
              description="پس از پرسش و پاسخ کاربران با یارهای هوشمند، گزارش مصرف در اینجا نمایش داده می‌شود."
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--sk-border)', textAlign: 'right' }}>
                    <th style={{ padding: '8px 10px' }}>تاریخ</th>
                    <th style={{ padding: '8px 10px' }}>شناسه نوبت</th>
                    <th style={{ padding: '8px 10px' }}>وضعیت</th>
                    <th style={{ padding: '8px 10px' }}>توکن‌ها (ورودی / خروجی)</th>
                    <th style={{ padding: '8px 10px' }}>مجموع مصرف (توکن)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.turnId} style={{ borderBottom: '1px solid var(--sk-border)' }}>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {displayJalaliDateTime(row.createdAt)}
                      </td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>
                        {row.turnId.slice(0, 8)}…
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <V2Badge tone={row.state === 'settled' ? 'success' : 'warn'}>
                          {row.stateLabel}
                        </V2Badge>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {faDigits(row.usage.inputTokens ?? '۰')} /{' '}
                        {faDigits(row.usage.outputTokens ?? '۰')}
                      </td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>
                        {faDigits(
                          (
                            (row.usage.inputTokens ? BigInt(row.usage.inputTokens) : 0n) +
                            (row.usage.outputTokens ? BigInt(row.usage.outputTokens) : 0n)
                          ).toString(),
                        )}{' '}
                        توکن
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </V2Card>
    </div>
  );
}

function RetentionTab() {
  const provider = useAiProvider();
  const settings = useAiResource((signal) => provider.getSettings({ signal }), [provider]);
  const [days, setDays] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DescribedError | null>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    const value = Number(days);
    if (!Number.isInteger(value)) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await provider.updateSettings({ retentionDays: value });
      setSaved(true);
      settings.reload();
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  }

  if (settings.state.status === 'loading') {
    return (
      <V2Card>
        <div className="ai-live__pad" role="status">
          در حال دریافت تنظیمات…
        </div>
      </V2Card>
    );
  }

  return (
    <div className="ai-live">
      <V2Card>
        <V2CardHeader title="نگهداری و پاک‌سازی سوابق پاسخ‌ها" icon="calendar" />
        <div className="ai-live__pad">
          {settings.state.status === 'ready' ? (
            <>
              <label className="ai-live__row">
                <span>مدت زمان نگهداری پاسخ‌ها:</span>
                <input
                  className="sk-input"
                  type="number"
                  min={1}
                  max={30}
                  inputMode="numeric"
                  value={days ?? String(settings.state.data.retentionDays)}
                  onChange={(event) => setDays(event.target.value)}
                  style={{ width: 100 }}
                />
                <span>روز</span>
                <V2Button
                  variant="primary"
                  size="sm"
                  disabled={busy || days === null}
                  onClick={() => void save()}
                >
                  ذخیره مدت نگهداری
                </V2Button>
              </label>

              <p className="ai-live__hint" style={{ marginTop: 8 }}>
                ذخیرهٔ این مقدار فقط بازهٔ مجاز را تغییر می‌دهد؛ پاک‌سازی پاسخ‌های منقضی را Worker
                پس‌زمینه انجام می‌دهد. بر اساس ضوابط حریم خصوصی، مدت نگهداری فقط می‌تواند کمتر شود و
                از ۳۰ روز بیشتر نخواهد بود.
              </p>

              {saved ? (
                <p role="status" style={{ color: 'var(--sk-success)' }}>
                  تنظیم با موفقیت ذخیره شد.
                </p>
              ) : null}
              {error ? (
                <p role="alert" style={{ color: 'var(--sk-danger)' }}>
                  {error.message}
                  {error.requestId ? ` (کد پیگیری: ${error.requestId})` : ''}
                </p>
              ) : null}
            </>
          ) : (
            <p role="alert">{settings.state.error.message}</p>
          )}
        </div>
      </V2Card>
    </div>
  );
}
